import React, { useEffect, useState } from "react";
import "./Nad.css";
import DynamicBanner from "../../include/DynamicBanner";
import api from "../../../../api";

const API_URL = "/api/sitepages/academic/nad";
const BASE_URL = process.env.REACT_APP_API_URL;

function NadPage() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(API_URL);
        setPageData(res.data);
      } catch (err) {
        console.error("Fetching NAD error:", err);
        setError("Failed to load content.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <div className="text-center py-5">Loading...</div>;
  if (error)
    return <div className="text-center py-5 text-danger">Error: {error}</div>;
  if (!pageData) return null;

  const banner = pageData.banner_data || {};
  const page = pageData.page_content || {};
  const intro = page.intro_section || {};
  const reg = page.registration_process || {};
  const nadCell = page.nad_cell || {};
  const scheme = page.nad_scheme || {};
  const contact = page.contact_section || {};

  const introDetails = Array.isArray(intro.details) ? intro.details : [];
  const regButtons = reg.buttons || [];
  const videos = Array.isArray(scheme.videos) ? scheme.videos : [];
  const contacts = Array.isArray(contact.contact_details)
    ? contact.contact_details
    : Object.values(contact.contact_details || {});

  return (
    <>
      {/* Dynamic Banner with Mobile Image Support */}
      <DynamicBanner
        title={banner.title}
        bgImage={banner.images}
        bgImageMobile={banner.mobile_images}
      />

      <section className="nad-page">
        <div className="container">
          {/* Intro Section */}
          <div className="sec-title text-center ">
            <h2>{intro.heading || "National Academic Depository (NAD)"}</h2>
          </div>

          <div className="row d-flex justify-content-center align-items-center flex-column-reverse flex-md-row mb-5">
            <div className="col-md-7 col-12">
              {introDetails.map((p, i) => (
                <p
                  key={i}
                  dangerouslySetInnerHTML={{ __html: p }}
                  style={{ textAlign: "justify" }}
                  className="mb-3"
                />
              ))}
            </div>

            <div className="col-md-5 col-12 mb-3 mb-md-0">
              {intro.image_url && (
                <div className="nad-abt-img-wrapper shadow-sm">
                  <img
                    src={`${BASE_URL}/public/${intro.image_url.replace(/^\//, "")}`}
                    className="img-fluid rounded"
                    alt={intro.alt_image || "NAD Image"}
                    style={{
                      height: "100%",
                      width: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Registration Process Card */}
          <div className="content-card-nad mt-3 mt-md-5 p-4 border rounded shadow-sm bg-white">
            <div className="sec-title text-center py-3">
              <h2 className="h3">{reg.heading}</h2>
            </div>

            <div className="row align-items-start">
              <div className="col-lg-7">
                {reg.content_1 && (
                  <div dangerouslySetInnerHTML={{ __html: reg.content_1 }} />
                )}

                <div className="links mt-3">
                  {regButtons.map((btn, i) => (
                    <a
                      key={i}
                      href={btn.link}
                      target={btn.target}
                      rel="noreferrer noopener"
                      className="btn link-primary me-3 mb-2"
                    >
                      <i className="fa fa-external-link me-2"></i> {btn.text}
                    </a>
                  ))}
                </div>

                {reg.content_2 && (
                  <div
                    className="mt-3"
                    dangerouslySetInnerHTML={{ __html: reg.content_2 }}
                  />
                )}
              </div>

              <div className="col-lg-5">
                <div className="highlight-box info p-3 rounded">
                  <div className="highlight-content">
                    <p className="mb-2">
                      <strong>
                        <i className="fa fa-info-circle  me-2"></i>
                        Already Registered?
                      </strong>
                    </p>
                    {reg.already_registered && (
                      <div
                        className="small"
                        dangerouslySetInnerHTML={{
                          __html: reg.already_registered,
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* NAD Cell Description */}
          <div className="sec-title text-center">
            <h2>{nadCell.heading}</h2>
          </div>
          <p style={{ textAlign: "justify" }}>{nadCell.content}</p>

          {/* NAD-ABC Scheme Videos Section */}
          <div className="sec-title text-center ">
            <h2>{scheme.heading}</h2>
          </div>

          <div className="row d-flex justify-content-center">
            {videos.map((vid, i) => {
              let videoUrl = "";
              let thumbUrl = "";

              // Handle Video URL
              if (vid.type === "upload") {
                videoUrl = `${BASE_URL}/public/${vid.video_path?.replace(/^\//, "")}`;
              } else {
                videoUrl = vid.link;
              }

              // Handle Thumbnail URL
              if (vid.thumbnail) {
                thumbUrl = `${BASE_URL}/public/${vid.thumbnail.replace(/^\//, "")}`;
              } else if (vid.type === "link" && vid.link) {
                const ytId = vid.link.includes("v=")
                  ? vid.link.split("v=")[1].split("&")[0]
                  : vid.link.split("/").pop().split("?")[0];
                thumbUrl = `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
              } else {
                thumbUrl = "/assets/images/video-placeholder.jpg";
              }

              return (
                <div className="col-md-4 mb-4" key={i}>
                  <a
                    href={videoUrl}
                    className="glightbox3"
                    data-type="video"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div
                      className="video-card position-relative shadow-sm"
                      style={{ borderRadius: "10px", overflow: "hidden" }}
                    >
                      <img
                        src={thumbUrl}
                        alt="Video Thumbnail"
                        className="thumbnail"
                        style={{
                          width: "100%",
                          height: "220px",
                          objectFit: "cover",
                        }}
                      />
                      <img
                        src="/assets/images/youtube-logo-video-player.png"
                        alt="Play Icon"
                        className="play-icon position-absolute top-50 start-50 translate-middle"
                        style={{ width: "60px", zIndex: 2 }}
                      />
                      {vid.title && (
                        <div className="video-title-text p-2 bg-white text-center">
                          <p className="mb-0 text-dark fw-bold small">
                            {vid.title}
                          </p>
                        </div>
                      )}
                    </div>
                  </a>
                </div>
              );
            })}
          </div>

          {/* Contact Details Section */}
          <div className="sec-title text-center ">
            <h2>{contact.heading}</h2>
          </div>

          <div className="row d-flex justify-content-center g-4">
            {contacts.map((c, i) => (
              <div className="col-md-4 col-12" key={i}>
                <div className="card contact-card-nad h-100 shadow-sm border-0">
                  <div className="card-header bg-primary text-white py-3">
                    <h5 className="mb-0 fs-6 text-white">
                      <i className="fa fa-user me-2"></i> {c.name}
                    </h5>
                  </div>
                  <div className="card-body">
                    <p className="mb-2">
                      <i className="fa fa-briefcase text-muted me-2"></i>
                      <strong>Designation:</strong> {c.designation}
                    </p>
                    <p className="mb-2">
                      <i className="fa fa-phone text-muted me-2"></i>
                      <strong>Phone:</strong> {c.phone}
                    </p>
                    <p className="mb-0">
                      <i className="fa fa-envelope text-muted me-2"></i>
                      <strong>Email:</strong> {c.email}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default NadPage;
